import React, { useState, useEffect } from 'react';
import { Play, Flag, Calculator } from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer/VideoPlayer';
import BackToDashboard from '../components/BackToDashboard';
import { usePitchAnalysisStore } from '../stores/pitchAnalysisStore';
import { useSpeedAnalysisStore } from '../stores/speedAnalysisStore';
import { useVideoStore } from '../stores/videoStore';
import { cn } from '../utils/cn';

const PitchPage = () => {
  const {
    startFrame,
    endFrame,
    steps,
    fps,
    setStartFrame,
    setEndFrame,
    setSteps,
    setFps,
    reset,
    calculateResults,
  } = usePitchAnalysisStore();

  const speedFps = useSpeedAnalysisStore(state => state.fps);
  const speedResults = useSpeedAnalysisStore(state => state.calculateResults());

  const { currentFrame } = useVideoStore();
  const [showResults, setShowResults] = useState(false);

  // Sync fps with speed analysis store
  useEffect(() => {
    setFps(speedFps);
  }, [speedFps, setFps]);

  const handleStartFrame = () => {
    setStartFrame(currentFrame);
  };

  const handleEndFrame = () => {
    setEndFrame(currentFrame);
  };

  const handleAnalyze = () => {
    setShowResults(true);
  };

  const handleReset = () => {
    reset();
    setShowResults(false);
  };

  const results = showResults ? calculateResults() : null;
  const canAnalyze = startFrame !== null && endFrame !== null && steps > 0;

  // ストライドを1cm伸ばした場合の予測計算
  const calculatePredictedTime = () => {
    if (!results || !speedResults) return null;

    // 現在のストライド（m）
    const currentStride = results.stride;
    // 1cm増加したストライド（m）
    const newStride = currentStride + 0.01;
    // ピッチは維持
    const currentPitch = results.pitch;
    // 新しい速度 = 新しいストライド × 現在のピッチ
    const newSpeed = newStride * currentPitch;
    // 100mの予測タイム
    return (100 / newSpeed).toFixed(2);
  };

  const predictedTime = calculatePredictedTime();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      <h1 className="text-3xl font-bold text-gray-900">ピッチ/ストライド分析</h1>
      
      <div className="mt-6 space-y-6">
        <VideoPlayer />
        
        <div className="bg-white p-6 rounded-lg shadow space-y-6">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handleStartFrame}
              className={cn(
                "btn btn-primary space-x-2",
                startFrame !== null && "bg-green-600 hover:bg-green-700"
              )}
            >
              <Play className="w-4 h-4" />
              <span>開始フレーム {startFrame !== null ? `(${startFrame})` : ''}</span>
            </button>
            
            <button
              onClick={handleEndFrame}
              className={cn(
                "btn btn-primary space-x-2",
                endFrame !== null && "bg-red-600 hover:bg-red-700"
              )}
            >
              <Flag className="w-4 h-4" />
              <span>終了フレーム {endFrame !== null ? `(${endFrame})` : ''}</span>
            </button>

            <button
              onClick={handleReset}
              className="btn btn-secondary"
            >
              リセット
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                歩数
              </label>
              <select
                value={steps}
                onChange={(e) => setSteps(Number(e.target.value))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                {[1, 2, 4, 6, 8, 10].map((value) => (
                  <option key={value} value={value}>
                    {value} 歩
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                フレームレート (fps)
              </label>
              <div className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700 sm:text-sm">
                {fps} fps (Speedタブと同期)
              </div>
            </div>
          </div>

          {/* スピード表示エリア */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">スピード分析の結果</h3>
            <div className="text-2xl font-bold text-gray-900">
              {speedResults ? `${speedResults.speed.toFixed(2)} m/s` : '未測定'}
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleAnalyze}
              disabled={!canAnalyze}
              className={cn(
                "btn btn-primary px-8 py-3 text-lg",
                !canAnalyze && "opacity-50 cursor-not-allowed"
              )}
            >
              <Calculator className="w-5 h-5 mr-2" />
              分析開始
            </button>
          </div>

          {results && (
            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">分析結果</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-sm text-gray-500">フレーム数</div>
                  <div className="text-2xl font-bold text-gray-900">{results.frameCount}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-sm text-gray-500">ピッチ (歩/秒)</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {results.pitch.toFixed(2)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="text-sm text-gray-500">ストライド (m)</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {results.stride.toFixed(2)}
                  </div>
                </div>
                {predictedTime && (
                  <div className="bg-white p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500">
                      ストライド+1cmでの100m予測 (秒)
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {predictedTime}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-700">
            <h4 className="font-medium mb-2">操作手順</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>測定範囲を「開始」と「終了」ボタンをタップして決定</li>
              <li>歩数を入力</li>
              <li>「分析開始」ボタンをクリック</li>
              <li>計算された分析結果を確認</li>
            </ol>
            <p className="mt-2 text-xs">※データがおかしい場合はフレームレートを60に戻し、最初からやり直してください。</p>
          </div>
        </div>
      </div>
      <BackToDashboard />
    </div>
  );
};

export default PitchPage;